import React, {useState, useCallback, useMemo, useEffect} from "react";
import {Row, Col, Card, CardHeader, CardBody, CardTitle, Button} from "reactstrap";
import {BackgroundColorContext} from "../../../contexts/BackgroundColorContext";
import FpsMetricsVertical from "./FpsMetricsVertical";
import NetworkMetricsVertical from "./NetworkMetricsVertical";
import PrimaryMetricsChart from "./PrimaryMetricsChart";
import AntRxFailuresReport from "../AntRxFailuresReport";
import GenericValueBarChartReport from "../GenericValueBarChartReport";
import LatencyReport from "../LatencyReport";
import WorldClockOffsetsReport from "../WorldClockOffsetsReport";
import GroupEventDetails from "../GroupEventDetails";
import colors from "../../../styles/colors";
import {getCrowdednessScore, maxCrowdedness} from "../../../utils/crowdingUtils";
import AdvancedAnalysisButton from "../../AdvancedAnalysis/AdvancedAnalysisButton";
import AnalysisConfirmationDialog from "../../AdvancedAnalysis/AnalysisConfirmationDialog";
import AnalysisSidePanel from "../../AdvancedAnalysis/AnalysisSidePanel";
import {
  loadExistingAnalysis,
  createNewAnalysis,
  getCacheAge,
  loadDemoAnalysis
} from "../../../services/advancedAnalysisService";
import {AnalysisStatus, AnalysisResult} from "../../../types/advancedAnalysis";
import toast from "react-hot-toast";
import {normalizeBleDisconnects} from "../../../utils/bleDisconnectsTransform";
import {localizeDataPoints, localizeTimestamp} from "../../../utils/functions";
import {DataSeries, dataSeriesToPoints, dataSeriesLength} from "../../../utils/dataAlignment";

export interface DashboardReportProps {
  // Activity data
  world?: string;
  version?: string;
  xaxis?: DataSeries;

  // Graphics/FPS data
  fps?: {
    points: Array<[number, number]>;
    profile: string;
    resolution: string;
    pc1: number;
    avg: number;
    max: number;
    min: number;
    stdev: number;
  };

  // UDP Network data
  udpNetworkPackets?: {
    points: Array<[number, number, number, number, number]>; // [timestamp, stc, rxerrors, cts, txerrors]
    maxStc: number;
    avgStc: number;
    maxCts: number;
    avgCts: number;
    sumRxErrors: number;
    sumTxErrors: number;
  };

  // Disconnect events
  networkDisconnectsTcp?: {points: Array<[number, number]>};
  bleDisconnects?: {points: Array<[number, number]>};
  antDisconnects?: {points: Array<[number, number]>};

  // Secondary metrics
  antRxFailures?: Array<{
    channel: number;
    label: string;
    percentage?: number; // May be called 'percentage' or 'failureRate' in the data
    failureRate?: number; // May be called 'percentage' or 'failureRate' in the data
    points: Array<[number, number]>;
  }>;

  antFetTransferFails?: {
    points: Array<[number, number]>;
    showrate?: boolean;
  };

  bleFtmsResends?: {points: Array<[number, number]>};
  bleFtmsTrainerResets?: {points: Array<[number, number]>};
  networkTimeoutsUdp?: {points: Array<[number, number]>};
  networkErrorsUdp?: {points: Array<[number, number]>};
  networkDisconnectsUdp?: {points: Array<[number, number]>};
  networkConnectionAttemptsUdp?: {points: Array<[number, number]>};

  // TCP Network data (missing v3 chart data)
  networkTimeoutsTcp?: {points: Array<[number, number]>};
  networkErrorsTcp?: {points: Array<[number, number]>};

  // Latency data (missing v3 chart data)
  latencyTestFailures?: {
    points: Array<[number, number, number, number, number]>; // [time, min, avg, max, n]
    title?: string;
    dataSeriesKey?: string;
    seriesName?: string;
    color?: string;
  };
  worldClockOffsets?: {
    points: Array<[number, number]>; // [timestamp, offset]
    title?: string;
  };

  // Companion app data (missing v3 chart data)
  companionAppConnectionAttempts?: {points: Array<[number, number]>};

  // System and session data
  system?: any;
  session?: {
    antDevices?: any[];
    bleDevicesNative?: any[];
    bleDevicesCompanion?: any[];
    bleDevicesLan?: any[];
    zpDevices?: any[];
    durationInSeconds?: number;
  };

  // Group event data
  groupEventDetails?: any;

  // Advanced analysis props
  userId?: string;
  logId?: string;
  jwtToken?: string;
  analysisStatus?: AnalysisStatus;
  onAnalysisStatusChange?: (status: AnalysisStatus) => void;
  isPublic?: boolean;
  isDemo?: boolean;

  // Timezone localization
  timezoneOffsetMinutes?: number;
}

const DashboardReport: React.FC<DashboardReportProps> = (props) => {
  // Shared zoom state for synchronized zooming across all charts
  const [zoomState, setZoomState] = useState<{min?: number; max?: number}>({});

  // Advanced analysis state (shared from parent or local fallback)
  const analysisStatus = props.analysisStatus || "checking";
  const setAnalysisStatus = props.onAnalysisStatusChange || (() => {});
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showSidePanel, setShowSidePanel] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isLoadingAnalysis, setIsLoadingAnalysis] = useState(false);
  const [forceReanalyze, setForceReanalyze] = useState(false);

  // Callback to handle zoom changes from any chart
  const handleZoomChange = useCallback((newZoomState: {min?: number; max?: number}) => {
    setZoomState(newZoomState);
  }, []);

  // Callback to reset zoom on all charts
  const handleResetZoom = useCallback(() => {
    setZoomState({});
  }, []);

  // Note: Analysis existence check is now handled by parent LogReport component
  // to ensure shared status across all activities

  // Handle run analysis button click
  const handleRunAnalysis = useCallback(() => {
    setForceReanalyze(false);
    setShowConfirmationModal(true);
  }, []);

  // Handle view analysis button click
  const handleViewAnalysis = useCallback(async () => {
    if (!props.logId) {
      return;
    }

    // Open panel immediately with loading state
    setIsLoadingAnalysis(true);
    setShowSidePanel(true);

    try {
      let result: AnalysisResult;

      // For demo mode, load from public S3 bucket (no auth required)
      if (props.isDemo) {
        result = await loadDemoAnalysis(props.logId);
      } else {
        // For authenticated users, load from authenticated endpoint
        if (!props.userId || !props.jwtToken) {
          throw new Error("Authentication required");
        }
        result = await loadExistingAnalysis(props.userId, props.logId, props.jwtToken);
      }

      setAnalysisResult(result);
    } catch (error: any) {
      toast.error(error.error?.message || "Failed to load analysis");
      setShowSidePanel(false); // Close panel on error
    } finally {
      setIsLoadingAnalysis(false);
    }
  }, [props.userId, props.logId, props.jwtToken, props.isDemo]);

  // Handle confirmation modal confirm
  const handleConfirmAnalysis = useCallback(async () => {
    setShowConfirmationModal(false);

    if (!props.userId || !props.logId || !props.jwtToken) {
      return;
    }

    setIsAnalyzing(true);
    setAnalysisStatus("checking");
    toast.loading("Running advanced analysis... This may take up to 30 seconds.", {id: "analysis-loading"});

    try {
      // Perform analysis - API responds in < 30 seconds
      const result = forceReanalyze
        ? await createNewAnalysis(props.userId, props.logId, props.jwtToken)
        : await createNewAnalysis(props.userId, props.logId, props.jwtToken); // First-time analysis always creates new

      setAnalysisResult(result);
      setAnalysisStatus("exists");
      setShowSidePanel(true);
      toast.dismiss("analysis-loading");
      toast.success("Analysis complete!");
    } catch (error: any) {
      setAnalysisStatus("error");
      toast.dismiss("analysis-loading");

      // Handle specific error cases
      if (error.error?.statusCode === 402) {
        toast.error("Insufficient tokens - please purchase more tokens to run analysis");
      } else if (error.error?.statusCode === 404) {
        toast.error("Log file not found - please try re-uploading");
      } else {
        toast.error(error.error?.message || "Failed to perform analysis");
      }
    } finally {
      setIsAnalyzing(false);
    }
  }, [props.userId, props.logId, props.jwtToken, forceReanalyze]);

  // Handle re-analyze from side panel
  const handleReanalyze = useCallback(() => {
    setShowSidePanel(false);
    setForceReanalyze(true);
    setShowConfirmationModal(true);
  }, []);

  // Normalize bluetooth disconnects data to handle both old and new formats, then localize
  const normalizedBleDisconnects = useMemo(() => {
    const normalized = normalizeBleDisconnects(props.bleDisconnects, props.version);
    if (!normalized?.points) {
      return normalized;
    }
    return {
      ...normalized,
      points: localizeDataPoints(normalized.points, props.timezoneOffsetMinutes || 0)
    };
  }, [props.bleDisconnects, props.version, props.timezoneOffsetMinutes]);

  // Device presence detection
  const devicePresence = useMemo(() => {
    const hasAntDevices = !!(props.session?.antDevices && props.session.antDevices.length > 0);

    const hasBleDevices = !!(
      (props.session?.bleDevicesNative && props.session.bleDevicesNative.length > 0) ||
      (props.session?.bleDevicesCompanion && props.session.bleDevicesCompanion.length > 0) ||
      (props.session?.bleDevicesLan && props.session.bleDevicesLan.length > 0)
    );

    const hasZpDevices = !!(props.session?.zpDevices && props.session.zpDevices.length > 0);

    return {hasAntDevices, hasBleDevices, hasZpDevices};
  }, [props.session]);

  // Localize all chart data points to user's timezone
  const timezoneOffset = props.timezoneOffsetMinutes || 0;

  const localizedFpsData = useMemo(() => {
    if (!props.fps?.points) {
      return props.fps;
    }
    return {
      ...props.fps,
      points: localizeDataPoints(props.fps.points, timezoneOffset)
    };
  }, [props.fps, timezoneOffset]);

  const localizedUdpData = useMemo(() => {
    if (!props.udpNetworkPackets?.points) {
      return props.udpNetworkPackets;
    }
    return {
      ...props.udpNetworkPackets,
      points: localizeDataPoints(props.udpNetworkPackets.points, timezoneOffset)
    };
  }, [props.udpNetworkPackets, timezoneOffset]);

  const localizedTcpDisconnects = useMemo(() => {
    if (!props.networkDisconnectsTcp?.points) {
      return props.networkDisconnectsTcp;
    }
    return {
      ...props.networkDisconnectsTcp,
      points: localizeDataPoints(props.networkDisconnectsTcp.points, timezoneOffset)
    };
  }, [props.networkDisconnectsTcp, timezoneOffset]);

  const localizedAntDisconnects = useMemo(() => {
    if (!props.antDisconnects?.points) {
      return props.antDisconnects;
    }
    return {
      ...props.antDisconnects,
      points: localizeDataPoints(props.antDisconnects.points, timezoneOffset)
    };
  }, [props.antDisconnects, timezoneOffset]);

  // Localize secondary metrics data
  const localizedAntRxFailures = useMemo(() => {
    if (!props.antRxFailures) {
      return props.antRxFailures;
    }
    return props.antRxFailures.map((rxFail) => ({
      ...rxFail,
      points: localizeDataPoints(rxFail.points, timezoneOffset)
    }));
  }, [props.antRxFailures, timezoneOffset]);

  const localizedAntFetTransferFails = useMemo(() => {
    if (!props.antFetTransferFails?.points) {
      return props.antFetTransferFails;
    }
    return {
      ...props.antFetTransferFails,
      points: localizeDataPoints(props.antFetTransferFails.points, timezoneOffset)
    };
  }, [props.antFetTransferFails, timezoneOffset]);

  const localizedBleFtmsResends = useMemo(() => {
    if (!props.bleFtmsResends?.points) {
      return props.bleFtmsResends;
    }
    return {
      ...props.bleFtmsResends,
      points: localizeDataPoints(props.bleFtmsResends.points, timezoneOffset)
    };
  }, [props.bleFtmsResends, timezoneOffset]);

  const localizedBleFtmsTrainerResets = useMemo(() => {
    if (!props.bleFtmsTrainerResets?.points) {
      return props.bleFtmsTrainerResets;
    }
    return {
      ...props.bleFtmsTrainerResets,
      points: localizeDataPoints(props.bleFtmsTrainerResets.points, timezoneOffset)
    };
  }, [props.bleFtmsTrainerResets, timezoneOffset]);

  const localizedNetworkTimeoutsUdp = useMemo(() => {
    if (!props.networkTimeoutsUdp?.points) {
      return props.networkTimeoutsUdp;
    }
    return {
      ...props.networkTimeoutsUdp,
      points: localizeDataPoints(props.networkTimeoutsUdp.points, timezoneOffset)
    };
  }, [props.networkTimeoutsUdp, timezoneOffset]);

  const localizedNetworkErrorsUdp = useMemo(() => {
    if (!props.networkErrorsUdp?.points) {
      return props.networkErrorsUdp;
    }
    return {
      ...props.networkErrorsUdp,
      points: localizeDataPoints(props.networkErrorsUdp.points, timezoneOffset)
    };
  }, [props.networkErrorsUdp, timezoneOffset]);

  const localizedNetworkDisconnectsUdp = useMemo(() => {
    if (!props.networkDisconnectsUdp?.points) {
      return props.networkDisconnectsUdp;
    }
    return {
      ...props.networkDisconnectsUdp,
      points: localizeDataPoints(props.networkDisconnectsUdp.points, timezoneOffset)
    };
  }, [props.networkDisconnectsUdp, timezoneOffset]);

  const localizedNetworkConnectionAttemptsUdp = useMemo(() => {
    if (!props.networkConnectionAttemptsUdp?.points) {
      return props.networkConnectionAttemptsUdp;
    }
    return {
      ...props.networkConnectionAttemptsUdp,
      points: localizeDataPoints(props.networkConnectionAttemptsUdp.points, timezoneOffset)
    };
  }, [props.networkConnectionAttemptsUdp, timezoneOffset]);

  const localizedNetworkTimeoutsTcp = useMemo(() => {
    if (!props.networkTimeoutsTcp?.points) {
      return props.networkTimeoutsTcp;
    }
    return {
      ...props.networkTimeoutsTcp,
      points: localizeDataPoints(props.networkTimeoutsTcp.points, timezoneOffset)
    };
  }, [props.networkTimeoutsTcp, timezoneOffset]);

  const localizedNetworkErrorsTcp = useMemo(() => {
    if (!props.networkErrorsTcp?.points) {
      return props.networkErrorsTcp;
    }
    return {
      ...props.networkErrorsTcp,
      points: localizeDataPoints(props.networkErrorsTcp.points, timezoneOffset)
    };
  }, [props.networkErrorsTcp, timezoneOffset]);

  const localizedLatencyTestFailures = useMemo(() => {
    if (!props.latencyTestFailures?.points) {
      return props.latencyTestFailures;
    }
    return {
      ...props.latencyTestFailures,
      points: localizeDataPoints(props.latencyTestFailures.points, timezoneOffset)
    };
  }, [props.latencyTestFailures, timezoneOffset]);

  const localizedWorldClockOffsets = useMemo(() => {
    if (!props.worldClockOffsets?.points) {
      return props.worldClockOffsets;
    }
    return {
      ...props.worldClockOffsets,
      points: localizeDataPoints(props.worldClockOffsets.points, timezoneOffset)
    };
  }, [props.worldClockOffsets, timezoneOffset]);

  const localizedCompanionAppConnectionAttempts = useMemo(() => {
    if (!props.companionAppConnectionAttempts?.points) {
      return props.companionAppConnectionAttempts;
    }
    return {
      ...props.companionAppConnectionAttempts,
      points: localizeDataPoints(props.companionAppConnectionAttempts.points, timezoneOffset)
    };
  }, [props.companionAppConnectionAttempts, timezoneOffset]);

  // Create axis series from xaxis data
  const axisSeries = useMemo(() => {
    if (!props.xaxis || dataSeriesLength(props.xaxis) === 0) {
      return undefined;
    }

    const xaxisPoints = dataSeriesToPoints(props.xaxis);
    const timelineData = xaxisPoints.map(([timestamp, value]) => ({
      timestamp: localizeTimestamp(timestamp, timezoneOffset),
      value: value || timestamp // Use the second value from the point, or timestamp as fallback
    }));
    const timeRange = {
      start: timelineData[0].timestamp,
      end: timelineData[timelineData.length - 1].timestamp
    };

    return {
      name: "xaxis",
      data: timelineData,
      timeRange
    };
  }, [props.xaxis, timezoneOffset]);

  // Calculate summary metrics
  const summaryMetrics = useMemo(() => {
    // FPS metrics
    const fpsMetrics = props.fps
      ? {
          profile: props.system.profile,
          resolution: props.system.resolution,
          pc1: props.fps.pc1,
          avg: props.fps.avg,
          max: props.fps.max,
          stdev: props.fps.stdev
        }
      : undefined;

    // UDP metrics with crowding calculation
    const udpMetrics = props.udpNetworkPackets
      ? {
          maxStc: props.udpNetworkPackets.maxStc,
          avgStc: props.udpNetworkPackets.avgStc,
          maxCts: props.udpNetworkPackets.maxCts,
          avgCts: props.udpNetworkPackets.avgCts,
          sumRxErrors: props.udpNetworkPackets.sumRxErrors,
          sumTxErrors: props.udpNetworkPackets.sumTxErrors,
          crowdingScore: getCrowdednessScore(
            props.udpNetworkPackets.avgStc,
            props.udpNetworkPackets.avgCts,
            maxCrowdedness
          ),
          crowdingTier: (() => {
            const score = getCrowdednessScore(
              props.udpNetworkPackets.avgStc,
              props.udpNetworkPackets.avgCts,
              maxCrowdedness
            );
            if (score >= 0.8) {
              return "Fondo";
            }
            if (score >= 0.6) {
              return "Packed";
            }
            if (score >= 0.4) {
              return "Crowded";
            }
            if (score >= 0.2) {
              return "Company";
            }
            return "Solo";
          })()
        }
      : undefined;

    // Device connectivity metrics
    const deviceMetrics = {
      hasAntDevices: devicePresence.hasAntDevices,
      hasBleDevices: devicePresence.hasBleDevices,
      hasZpDevices: devicePresence.hasZpDevices,
      totalTcpDisconnects: props.networkDisconnectsTcp?.points?.length || 0,
      totalBleDisconnects: normalizedBleDisconnects?.points?.length || 0,
      totalAntDisconnects: props.antDisconnects?.points?.length || 0
    };

    return {fpsMetrics, udpMetrics, deviceMetrics};
  }, [props, devicePresence, normalizedBleDisconnects]);

  // Check if we have any primary data to display
  const hasPrimaryData = !!(
    (props.fps?.points && props.fps.points.length > 0) ||
    (props.udpNetworkPackets?.points && props.udpNetworkPackets.points.length > 0) ||
    (props.networkDisconnectsTcp?.points && props.networkDisconnectsTcp.points.length > 0) ||
    (devicePresence.hasBleDevices && normalizedBleDisconnects?.points && normalizedBleDisconnects.points.length > 0) ||
    (devicePresence.hasAntDevices && props.antDisconnects?.points && props.antDisconnects.points.length > 0)
  );

  if (!hasPrimaryData) {
    return (
      <BackgroundColorContext.Consumer>
        {({color}) => (
          <Card>
            <CardHeader data-color={color} className="no-body">
              <CardTitle>Dashboard Unavailable</CardTitle>
            </CardHeader>
            <CardBody>
              <div className="text-center p-5">
                <i className="fa fa-chart-line fa-3x text-muted mb-4"></i>
                <h5>No Performance Data Available</h5>
                <p className="text-muted">
                  This log file doesn't contain enough performance metrics to generate a dashboard view. This might
                  occur with very short sessions or logs from older versions of the virtual cycling platform.
                </p>
                <p className="text-muted">
                  <small>Try the detailed charts view instead for a complete analysis.</small>
                </p>
              </div>
            </CardBody>
          </Card>
        )}
      </BackgroundColorContext.Consumer>
    );
  }

  return (
    <BackgroundColorContext.Consumer>
      {({color}) => (
        <div className="dashboard-with-panel">
          <div className={`dashboard-main-content ${showSidePanel ? "panel-open" : ""}`}>
            {/* Primary Performance Chart with FPS and Network metrics on the left */}
            <Row>
              {/* Metrics Sidebar Column */}
              <Col lg="2" md="2" sm="12">
                {/* FPS Metrics */}
                {summaryMetrics.fpsMetrics && (
                  <div className="mb-2">
                    <FpsMetricsVertical fpsMetrics={summaryMetrics.fpsMetrics} />
                  </div>
                )}

                {/* Network Metrics */}
                {summaryMetrics.udpMetrics && (
                  <div className="mb-2">
                    <NetworkMetricsVertical udpMetrics={summaryMetrics.udpMetrics} />
                  </div>
                )}
              </Col>

              {/* Main Chart Column */}
              <Col lg="10" md="10" sm="12">
                <div>
                  <div>
                    <p className="mb-0">
                      <small>
                        <strong>Graphics FPS</strong> (left axis) • <strong>Network Packets</strong> (right axis) •
                        <strong> Connection Events</strong> (scatter points) •
                        <span className="text-info ml-1">
                          <i className="fa fa-search-plus"></i> Click &amp; drag to zoom.
                        </span>
                      </small>
                    </p>
                  </div>
                  <div>
                    <PrimaryMetricsChart
                      fpsData={localizedFpsData}
                      udpData={localizedUdpData}
                      tcpDisconnects={localizedTcpDisconnects}
                      bleDisconnects={
                        devicePresence.hasBleDevices && normalizedBleDisconnects ? normalizedBleDisconnects : undefined
                      }
                      antDisconnects={devicePresence.hasAntDevices ? localizedAntDisconnects : undefined}
                      axisSeries={axisSeries}
                      zoomState={zoomState}
                      onZoomChange={handleZoomChange}
                      onResetZoom={handleResetZoom}
                      timezoneOffsetMinutes={timezoneOffset}
                    />
                  </div>
                </div>
              </Col>
            </Row>
            {/* Zoom Controls Row */}
            {(zoomState.min !== undefined || zoomState.max !== undefined) && (
              <Row className="mb-3">
                <Col className="text-right">
                  <Button
                    onClick={handleResetZoom}
                    color="primary"
                    data-color={color}
                    size="sm"
                    title="Reset zoom on all charts"
                  >
                    <i className="fa fa-search-minus mr-1"></i>
                    Reset Zoom
                  </Button>
                </Col>
              </Row>
            )}

            {/* Advanced Analysis CTA - Prominent Placement */}
            {!props.isPublic && props.logId && (
              <Row className="mb-4">
                <Col lg="12">
                  <div className="advanced-analysis-cta" data-color={color}>
                    <div className="cta-content">
                      <div className="cta-icon">
                        <i className="fa fa-chart-line"></i>
                      </div>
                      <div className="cta-text">
                        <h5 className="mb-1">
                          {(props.isDemo ? "exists" : analysisStatus) === "exists"
                            ? "View Intelligent Performance Insights"
                            : "Get Intelligent Performance Insights"}
                        </h5>
                        <p className="mb-0">
                          Advanced Analysis performs 50+ automated checks to detect performance issues and provides
                          actionable recommendations for graphics, sensors, network, and hardware.
                        </p>
                      </div>
                      <div className="cta-button">
                        <AdvancedAnalysisButton
                          status={props.isDemo ? "exists" : analysisStatus}
                          onRunAnalysis={handleRunAnalysis}
                          onViewAnalysis={handleViewAnalysis}
                          size="lg"
                        />
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            )}

            {/* Analysis Confirmation Modal */}
            {props.userId && props.logId && props.jwtToken && (
              <AnalysisConfirmationDialog
                isOpen={showConfirmationModal}
                onConfirm={handleConfirmAnalysis}
                onCancel={() => setShowConfirmationModal(false)}
              />
            )}

            {/* Group Event Details section - only show if data exists */}
            {props.groupEventDetails && (
              <div className="mt-4">
                <Row>
                  <Col>
                    <GroupEventDetails groupEventDetails={props.groupEventDetails} />
                  </Col>
                </Row>
              </div>
            )}

            {/* ANT+ Communications section - always show */}
            <div className="mt-4">
              <Row className="mb-3">
                <Col>
                  <div className="devices-divider"></div>
                  <h4 className="text-muted">
                    <i className="fa fa-signal mr-2"></i>
                    ANT+ Communications
                  </h4>
                </Col>
              </Row>

              <Row>
                {/* ANT+ Interference - 4 Channels */}
                {(() => {
                  const channelDataMap: {[key: number]: any} = {};

                  if (localizedAntRxFailures && localizedAntRxFailures.length > 0) {
                    localizedAntRxFailures.forEach((rxFail, index) => {
                      let channel = rxFail.channel;

                      if (!channel && rxFail.label) {
                        const channelMatch = rxFail.label.match(/Channel (\d+)/);
                        if (channelMatch) {
                          channel = parseInt(channelMatch[1]);
                        }
                      }

                      if (!channel) {
                        channel = index + 1;
                      }

                      if (channel >= 1 && channel <= 4) {
                        channelDataMap[channel - 1] = rxFail;
                      }
                    });
                  }

                  const charts = [];
                  const antRxFailColors = [colors.sonicblue, colors.beige, colors.seafoamgreen, colors.salmonpink];

                  for (let i = 0; i < 4; i++) {
                    const chartData = channelDataMap[i];

                    if (chartData) {
                      charts.push(
                        <Col lg="6" md="12" className="mb-4" key={`ant-rx-${i}`}>
                          <AntRxFailuresReport
                            {...chartData}
                            axisSeries={axisSeries}
                            title={`ANT+ Interference ${chartData.label}`}
                            failureRate={chartData.percentage || chartData.failureRate || 0}
                            color={antRxFailColors[i]}
                          />
                        </Col>
                      );
                    } else {
                      const startTime = axisSeries?.timeRange?.start || Date.now() - 3600000;
                      const endTime = axisSeries?.timeRange?.end || Date.now();
                      const emptyPoints: Array<[number, number]> = [
                        [startTime, 0],
                        [endTime, 0]
                      ];

                      charts.push(
                        <Col lg="6" md="12" className="mb-4" key={`ant-rx-${i}`}>
                          <AntRxFailuresReport
                            label={`Channel ${i + 1} - None`}
                            points={emptyPoints}
                            failureRate={0}
                            axisSeries={axisSeries}
                            title={`ANT+ Interference Channel ${i + 1} - None`}
                            color={antRxFailColors[i]}
                          />
                        </Col>
                      );
                    }
                  }

                  return charts;
                })()}

                {/* ANT+ FET Transfer Failures */}
                <Col lg="6" md="12" className="mb-4">
                  <GenericValueBarChartReport
                    points={localizedAntFetTransferFails?.points || []}
                    title="ANT+ FET Transfer Failures (Gradient/Resistance Change Failures)"
                    dataSeriesKey="fails"
                    seriesName="failure"
                    color={colors.orange}
                    tooltipId="antFetTransferFailures"
                    axisSeries={axisSeries}
                    showrate={localizedAntFetTransferFails?.showrate}
                    durationInSeconds={props.session?.durationInSeconds}
                  />
                </Col>

                {/* ANT+ Dropouts (Disconnects) */}
                <Col lg="6" md="12" className="mb-4">
                  <GenericValueBarChartReport
                    points={localizedAntDisconnects?.points || []}
                    title="ANT+ Dropouts"
                    dataSeriesKey="disconnects"
                    seriesName="disconnect"
                    color={colors.fiestared}
                    tooltipId="antDropouts"
                    axisSeries={axisSeries}
                    durationInSeconds={props.session?.durationInSeconds}
                  />
                </Col>
              </Row>
            </div>

            {/* Bluetooth Communications section - always show */}
            <div className="mt-4">
              <Row className="mb-3">
                <Col>
                  <div className="devices-divider"></div>
                  <h4 className="text-muted">
                    <i className="fab fa-bluetooth mr-2"></i>
                    Bluetooth Communications
                  </h4>
                </Col>
              </Row>

              <Row>
                {/* Bluetooth Disconnects */}
                <Col lg="4" md="12" className="mb-4">
                  <GenericValueBarChartReport
                    points={normalizedBleDisconnects?.points || []}
                    title="Bluetooth Disconnects"
                    dataSeriesKey="disconnects"
                    seriesName="disconnect"
                    color={colors.tacxblue}
                    tooltipId="bluetoothDisconnects"
                    axisSeries={axisSeries}
                    durationInSeconds={props.session?.durationInSeconds}
                  />
                </Col>

                {/* Bluetooth FTMS Message Resends */}
                <Col lg="4" md="12" className="mb-4">
                  <GenericValueBarChartReport
                    points={localizedBleFtmsResends?.points || []}
                    title="Bluetooth FTMS Message Resends"
                    dataSeriesKey="value"
                    seriesName="resend"
                    color={colors.sonicblue}
                    tooltipId="bluetoothFtmsResends"
                    axisSeries={axisSeries}
                    durationInSeconds={props.session?.durationInSeconds}
                  />
                </Col>

                {/* Bluetooth Trainer Resets */}
                <Col lg="4" md="12" className="mb-4">
                  <GenericValueBarChartReport
                    points={localizedBleFtmsTrainerResets?.points || []}
                    title="Bluetooth Trainer Resets"
                    dataSeriesKey="value"
                    seriesName="reset"
                    color={colors.lightblue}
                    tooltipId="bluetoothTrainerResets"
                    axisSeries={axisSeries}
                    durationInSeconds={props.session?.durationInSeconds}
                  />
                </Col>
              </Row>
            </div>

            {/* UDP Network Diagnostics section */}
            {(props.networkTimeoutsUdp?.points ||
              props.networkErrorsUdp?.points ||
              props.networkDisconnectsUdp?.points ||
              props.networkConnectionAttemptsUdp?.points) && (
              <div className="mt-4">
                <Row className="mb-3">
                  <Col>
                    <div className="devices-divider"></div>
                    <h4 className="text-muted">
                      <i className="fa fa-network-wired mr-2"></i>
                      UDP Communications
                    </h4>
                  </Col>
                </Row>

                <Row>
                  {/* UDP Timeouts */}
                  <Col lg="6" md="6" className="mb-4">
                    <GenericValueBarChartReport
                      points={localizedNetworkTimeoutsUdp?.points || []}
                      title="UDP Network Timeouts"
                      dataSeriesKey="timeouts"
                      seriesName="timeout"
                      color={colors.yellow}
                      tooltipId="networkTimeoutsUdp"
                      axisSeries={axisSeries}
                      durationInSeconds={props.session?.durationInSeconds}
                    />
                  </Col>

                  {/* UDP Errors */}
                  <Col lg="6" md="6" className="mb-4">
                    <GenericValueBarChartReport
                      points={localizedNetworkErrorsUdp?.points || []}
                      title="UDP Network Errors"
                      dataSeriesKey="errors"
                      seriesName="error"
                      color={colors.purple}
                      tooltipId="networkErrorsUdp"
                      axisSeries={axisSeries}
                      durationInSeconds={props.session?.durationInSeconds}
                    />
                  </Col>

                  {/* UDP Disconnects */}
                  <Col lg="6" md="6" className="mb-4">
                    <GenericValueBarChartReport
                      points={localizedNetworkDisconnectsUdp?.points || []}
                      title="UDP Network Disconnects"
                      dataSeriesKey="disconnects"
                      seriesName="disconnect"
                      color={colors.fiestared}
                      tooltipId="udpNetworkDisconnects"
                      axisSeries={axisSeries}
                      durationInSeconds={props.session?.durationInSeconds}
                    />
                  </Col>

                  {/* UDP Network Connects */}
                  <Col lg="6" md="6" className="mb-4">
                    <GenericValueBarChartReport
                      points={localizedNetworkConnectionAttemptsUdp?.points || []}
                      title="UDP Network Connects (Many entries here is normal)"
                      dataSeriesKey="connection"
                      seriesName="connection"
                      color={colors.lavender}
                      tooltipId="udpNetworkConnectionAttempts"
                      axisSeries={axisSeries}
                      durationInSeconds={props.session?.durationInSeconds}
                    />
                  </Col>
                </Row>
              </div>
            )}

            {/* TCP Network Diagnostics section */}
            {(props.networkTimeoutsTcp?.points ||
              props.networkErrorsTcp?.points ||
              props.networkDisconnectsTcp?.points) && (
              <div className="mt-4">
                <Row className="mb-3">
                  <Col>
                    <div className="devices-divider"></div>
                    <h4 className="text-muted">
                      <i className="fa fa-network-wired mr-2"></i>
                      TCP Communications
                    </h4>
                  </Col>
                </Row>

                <Row>
                  {/* TCP Timeouts */}
                  <Col lg="4" md="6" className="mb-4">
                    <GenericValueBarChartReport
                      points={localizedNetworkTimeoutsTcp?.points || []}
                      title="TCP Network Timeouts"
                      dataSeriesKey="timeouts"
                      seriesName="timeout"
                      color={colors.salmonpink}
                      tooltipId="networkTimeoutsTcp"
                      axisSeries={axisSeries}
                      durationInSeconds={props.session?.durationInSeconds}
                    />
                  </Col>

                  {/* TCP Errors */}
                  <Col lg="4" md="6" className="mb-4">
                    <GenericValueBarChartReport
                      points={localizedNetworkErrorsTcp?.points || []}
                      title="TCP Network Errors"
                      dataSeriesKey="errors"
                      seriesName="error"
                      color={colors.magenta}
                      tooltipId="networkErrorsTcp"
                      axisSeries={axisSeries}
                      durationInSeconds={props.session?.durationInSeconds}
                    />
                  </Col>

                  {/* TCP Disconnects */}
                  <Col lg="4" md="6" className="mb-4">
                    <GenericValueBarChartReport
                      points={localizedTcpDisconnects?.points || []}
                      title="TCP Network Disconnects"
                      dataSeriesKey="disconnects"
                      seriesName="disconnect"
                      color={colors.fiestared}
                      tooltipId="tcpNetworkDisconnects"
                      axisSeries={axisSeries}
                      durationInSeconds={props.session?.durationInSeconds}
                    />
                  </Col>
                </Row>
              </div>
            )}

            {/* Network Latency Analysis section - always show */}
            <div className="mt-4">
              <Row className="mb-3">
                <Col>
                  <div className="devices-divider"></div>
                  <h4 className="text-muted">
                    <i className="fa fa-clock mr-2"></i>
                    Network Latency Analysis
                  </h4>
                </Col>
              </Row>

              <Row>
                {/* Latency Test Failures */}
                <Col lg="6" md="12" className="mb-4">
                  {localizedLatencyTestFailures ? (
                    <LatencyReport
                      {...localizedLatencyTestFailures}
                      title="Latency Test Failures"
                      axisSeries={axisSeries}
                    />
                  ) : (
                    <LatencyReport title="Latency Test Failures" points={[]} axisSeries={axisSeries} />
                  )}
                </Col>

                {/* World Clock Offsets */}
                <Col lg="6" md="12" className="mb-4">
                  {localizedWorldClockOffsets ? (
                    <WorldClockOffsetsReport
                      {...localizedWorldClockOffsets}
                      title="World Clock Offsets"
                      axisSeries={axisSeries}
                    />
                  ) : (
                    <WorldClockOffsetsReport title="World Clock Offsets" points={[]} axisSeries={axisSeries} />
                  )}
                </Col>
              </Row>
            </div>

            {/* Companion App Connection Analysis section - always show */}
            <div className="mt-4">
              <Row className="mb-3">
                <Col>
                  <div className="devices-divider"></div>
                  <h4 className="text-muted">
                    <i className="fa fa-mobile-alt mr-2"></i>
                    Companion App Connection Analysis
                  </h4>
                </Col>
              </Row>

              <Row>
                {/* Companion App Connection Attempts */}
                <Col lg="12" md="12" className="mb-4">
                  <GenericValueBarChartReport
                    points={localizedCompanionAppConnectionAttempts?.points || []}
                    title="Companion App Connection Attempts (60s intervals)"
                    dataSeriesKey="value"
                    seriesName="attempt"
                    color={colors.magenta}
                    tooltipId="companionAppConnectionAttempts"
                    axisSeries={axisSeries}
                    durationInSeconds={props.session?.durationInSeconds}
                  />
                </Col>
              </Row>
            </div>
          </div>

          {/* Analysis Side Panel - outside main content for push-left layout */}
          {((props.userId && props.logId && props.jwtToken) || props.isDemo) && (
            <AnalysisSidePanel
              isOpen={showSidePanel}
              analysisResult={analysisResult}
              isLoading={isLoadingAnalysis}
              onClose={() => setShowSidePanel(false)}
              onReanalyze={handleReanalyze}
              cacheAgeMinutes={
                analysisResult && props.userId && props.logId ? getCacheAge(props.userId, props.logId) : null
              }
              isDemo={props.isDemo}
            />
          )}
        </div>
      )}
    </BackgroundColorContext.Consumer>
  );
};

export default DashboardReport;
